import Image from "next/image";

import Marquee from "@/components/magicui/marquee";
import SectionTag from "@/components/seo/SectionTag";
import VideoModalWithThumbnail from "@/components/seo/VideoModalWithThumbnail";
import { cn } from "@/lib/utils";

const SuccessStories = ({ city, stateAbbr, industry }: { city?: string; stateAbbr?: string; industry: string }) => {
  const headlineText =
    city && stateAbbr
      ? `How ${city}, ${stateAbbr} Business Owners Succeed With Rebolt`
      : `How ${industry} Business Owners Succeed With Rebolt`;
  return (
    <div className="bg-ash-100 w-full">
      <section className="flex flex-col py-12 relative container mx-auto">
        <Image
          src="/background-chevs.svg"
          alt="background-chevrons"
          width={425}
          height={425}
          className="absolute w-auto h-auto top-0 right-0 pointer-events-none"
        />
        <SectionTag text="Our Success Stories" className="" />
        <h2 className="text-3xl lg:text-4xl text-ash-800 font-semibold mt-4 mb-12 max-w-lg">{headlineText}</h2>

        <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 max-w-7xl lg:mx-auto">
          <div className="bg-white p-10 border border-ash-800/40 z-1 rounded-[20px] col-span-1 lg:col-span-7">
            <h3 className="text-lg lg:text-xl font-semibold">Built for Search Engines</h3>
            <p className="text-ash-900/75 text-sm mb-12">Real data results from real customers</p>
            <div className="flex flex-col lg:relative px-0">
              <div className="relative rounded-[20px] border mx-auto lg:mx-0 border-ash-700/40 lg:absolute lg:top-0 lg:left-0 w-full lg:w-5/6">
                <Image
                  src="/trending-analytics-1.png"
                  alt={`Google search console results for ${industry} website`}
                  width={800}
                  height={400}
                  className="w-full h-full object-cover rounded-[20px]"
                  sizes="(max-width: 768px) 90vw, 800px"
                  loading="lazy"
                />
                <div className="absolute top-5 left-5 bg-white backdrop-blur-xs text-ash-800 border border-ash-600/40 font-semibold px-3 py-1.5 rounded-lg text-sm">
                  First 3 Months With Rebolt
                </div>
              </div>
              <div className="relative w-full lg:w-5/6 rounded-[20px] mt-8 lg:mt-0 mx-auto border border-ash-700/40 lg:absolute lg:top-40 lg:right-0">
                <Image
                  src="/trending-analytics-2.png"
                  alt={`Google search console results for ${industry} website`}
                  width={800}
                  height={400}
                  className="w-full h-full object-cover rounded-[20px]"
                  sizes="(max-width: 768px) 90vw, 800px"
                  loading="lazy"
                />
                <div className="absolute top-5 left-5 bg-white backdrop-blur-xs text-ash-800 border border-ash-600/40 font-semibold px-3 py-1.5 rounded-lg text-sm">
                  First 3 Months With Rebolt
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden p-6 z-1 border border-ash-800/40 flex flex-col justify-start rounded-[20px] col-span-1 lg:col-span-3">
            <VideoModalWithThumbnail
              className="aspect-w-4 aspect-h-3"
              thumbnailUrl="https://ik.imagekit.io/4wu305uo4/testimonials/thumbnails/sebastian-g-thumbnail.png?updatedAt=1731604994899"
              videoUrl="https://ik.imagekit.io/4wu305uo4/testimonials/builtright-testimonial.mov/ik-video.mp4"
            />

            <div className="mt-6">
              <h3 className="text-lg font-semibold">Sebastian G.</h3>
              <h4 className="font-semibold">Padron Landscapes LLC</h4>
              <p className="text-ash-800/70 text-sm mt-1">Atlanta, GA</p>
            </div>
          </div>
        </div>

        <LogoMarquee className="mt-16 mb-4 lg:mt-20 lg:mb-16" />
      </section>
    </div>
  );
};

export default SuccessStories;

function LogoMarquee({ className }: { className?: string }) {
  return (
    <div className={cn("flex justify-center items-center w-full", className)}>
      <div className="relative w-full">
        <Marquee className="[--duration:40s] [--gap:6rem] max-h-14 grayscale whitespace-nowrap" repeat={6}>
          <span className="relative w-32 h-10">
            <Image src="/bcbalogo.jpg" alt="BCBA Logo" fill priority={false} sizes="120px" className="object-contain" />
          </span>
          <span className="relative w-32 h-10">
            <Image
              src="/ewing-logo.png"
              alt="Ewing Logo"
              fill
              priority={false}
              sizes="120px"
              className="object-contain"
            />
          </span>
          <span className="relative w-32 h-10">
            <Image
              src="/moovsoon.png"
              alt="Moovsoon Logo"
              fill
              priority={false}
              sizes="120px"
              className="object-contain"
            />
          </span>
          <span className="relative w-32 h-10">
            <Image src="/cpia.jpeg" alt="CPIA Logo" fill priority={false} sizes="120px" className="object-contain" />
          </span>
          <span className="relative w-32 h-10">
            <Image src="/ida.webp" alt="IDA Logo" fill priority={false} sizes="120px" className="object-contain" />
          </span>
          <span className="relative w-32 h-10">
            <Image
              src="/issa-logo.png"
              alt="ISSA Logo"
              fill
              priority={false}
              sizes="120px"
              className="object-contain"
            />
          </span>
          <span className="relative w-32 h-10">
            <Image src="/tulsa.png" alt="Tulsa Logo" fill priority={false} sizes="120px" className="object-contain" />
          </span>
        </Marquee>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(90deg, #f9fafb 10%, transparent 35%, transparent 65%, #f9fafb 90%)",
          }}
        />
      </div>
    </div>
  );
}
